import React, { useState } from 'react';
import { XIcon, ChevronLeftIcon, SaveIcon, UsersIcon, LayoutTemplateIcon, CreditCardIcon, SettingsIcon, PlusIcon, TrashIcon, GripVerticalIcon, CheckIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { CreateProcessusDialog } from './CreateProcessusDialog';
interface PermisTypeBuilderProps {
  typeData: {
    categorie: string;
    sousCategorie: string;
    type: string;
  };
  onClose: () => void;
}
type Tab = 'requester' | 'form' | 'payment' | 'settings';
export function PermisTypeBuilder({
  typeData,
  onClose
}: PermisTypeBuilderProps) {
  const {
    t
  } = useLanguage();
  const [activeTab, setActiveTab] = useState<Tab>('requester');
  // Requester config state
  const [requesterConfig, setRequesterConfig] = useState({
    self: true,
    family: false,
    other: false
  });
  // Payment config state
  const [paymentConfig, setPaymentConfig] = useState({
    enabled: false,
    timing: 'upfront' as 'upfront' | 'later',
    amount: ''
  });
  // Settings state
  const [settingsConfig, setSettingsConfig] = useState({
    department: 'Urbanisme',
    workflow: 'Permis de construction (standard)'
  });
  // Form builder mock state
  const [formSteps, setFormSteps] = useState([{
    id: '1',
    title: 'Informations générales'
  }, {
    id: '2',
    title: 'Documents requis'
  }]);
  const [showCreateProcessus, setShowCreateProcessus] = useState(false);
  const tabs: {
    id: Tab;
    label: string;
    Icon: typeof UsersIcon;
  }[] = [{
    id: 'requester',
    label: t('Demandeur', 'Requester'),
    Icon: UsersIcon
  }, {
    id: 'form',
    label: t('Formulaire', 'Form'),
    Icon: LayoutTemplateIcon
  }, {
    id: 'payment',
    label: t('Paiement', 'Payment'),
    Icon: CreditCardIcon
  }, {
    id: 'settings',
    label: t('Configuration', 'Settings'),
    Icon: SettingsIcon
  }];
  return <div className="space-y-6" data-id="element-4856" data-mp="1o3oupk06f6h7">
      {/* Header — breadcrumb, title and actions (kept above the configurer-style shell) */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-4857" data-mp="1t31jwbeispmy">
        <div className="flex items-center gap-3" data-id="element-4858" data-mp="1f3jpngiggk67">
          <button onClick={onClose} className="p-2 -ml-2 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100 transition-colors" aria-label={t('Retour', 'Back')} data-id="element-4859" data-mp="1f2qmsqmmaetc">
            <ChevronLeftIcon className="w-5 h-5" data-id="element-4860" data-mp="1a3xct86jh5xx" />
          </button>
          <div data-id="element-4861" data-mp="1a58g76w2oboi">
            <div className="flex items-center gap-2 text-xs font-medium text-slate-500 mb-0.5" data-id="element-4862" data-mp="1a5ryiopmx232">
              <span data-id="element-4863" data-mp="wapj9aywe0dl">{typeData.categorie}</span>
              <span className="text-slate-300" data-id="element-4864" data-mp="10dccngb7vtcw">/</span>
              <span data-id="element-4865" data-mp="10brj9u0n2ggm">{typeData.sousCategorie}</span>
            </div>
            <h2 className="text-xl font-bold text-slate-900 leading-none" data-id="element-4866" data-mp="meuxi6zdnp6l">
              {typeData.type}
            </h2>
          </div>
        </div>
        <div className="flex items-center gap-3" data-id="element-4867" data-mp="7re0goryx47a">
          <button onClick={onClose} className="px-4 py-2 text-sm font-semibold text-slate-600 hover:text-slate-900 transition-colors" data-id="element-4868" data-mp="7s5wg0ph93y3">
            {t('Annuler', 'Cancel')}
          </button>
          <button className="flex items-center gap-2 px-4 py-2 bg-[#4F46E5] text-white text-sm font-semibold rounded-lg hover:bg-[#4338CA] transition-colors shadow-sm" data-id="element-4869" data-mp="hnh3rxyk38vt">
            <SaveIcon className="w-4 h-4" data-id="element-4870" data-mp="qoxfqa50rj25" />
            {t('Enregistrer', 'Save')}
          </button>
        </div>
      </div>

      {/* Configurer-style shell: left section column + detached content container */}
      <div className="flex gap-6" data-id="element-4871" data-mp="10fjqnr1sjlei">
        {/* Section Switcher */}
        <div className="w-48 flex-shrink-0" data-id="element-4872" data-mp="vi9snn0c61f2">
          <div className="bg-white rounded-xl border border-slate-200 p-2 space-y-1 sticky top-4" data-id="element-4873" data-mp="vjku3g6b6x30">
            {tabs.map(({
            id,
            label,
            Icon
          }) => <button key={id} onClick={() => setActiveTab(id)} className={`w-full flex items-center gap-3 px-4 py-3 text-left text-sm font-semibold rounded-lg transition-colors ${activeTab === id ? 'bg-[#0F172A] text-white' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`} data-id="element-4874" data-mp="15gfpvrb76jdl">
                <Icon className="w-4 h-4 flex-shrink-0" data-id="element-4875" data-mp="38qar3rz78zeg" />
                {label}
              </button>)}
          </div>
        </div>

        {/* Content Area — detached container */}
        <div className="flex-1 bg-white rounded-xl border border-slate-200 p-8" data-id="element-4876" data-mp="2g1lwmbsnfsbo">
          {/* REQUESTER TAB */}
          {activeTab === 'requester' && <div className="space-y-6" data-id="element-4877" data-mp="2pwnnbc2kp2gd">
              <div data-id="element-4878" data-mp="2pzt7qe09bg5r">
                <h3 className="text-lg font-bold text-slate-900 mb-1" data-id="element-4879" data-mp="2tzjwkc683m7n">
                  {t('Qui peut faire la demande ?', 'Who can request?')}
                </h3>
                <p className="text-sm text-slate-500" data-id="element-4880" data-mp="59alqywon2ue">
                  {t('Définissez les rôles autorisés à soumettre ce type de permis.', 'Define the roles allowed to submit this permit type.')}
                </p>
              </div>

              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-id="element-4881" data-mp="1prd1dq7y2zxk">
                <label className="flex items-start gap-3 p-4 border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors" data-id="element-4882" data-mp="1kwfkxy8c5oan">
                  <div className="pt-0.5" data-id="element-4883" data-mp="1b0vrrjl5p3pl">
                    <input type="checkbox" checked={requesterConfig.self} onChange={e => setRequesterConfig({
                  ...requesterConfig,
                  self: e.target.checked
                })} className="w-4 h-4 text-[#4F46E5] rounded border-slate-300 focus:ring-[#4F46E5]" data-id="element-4884" data-mp="1b1xe4k7mhcmm" />
                  </div>
                  <div data-id="element-4885" data-mp="2rf5ek99r2keb">
                    <div className="text-sm font-semibold text-slate-900" data-id="element-4886" data-mp="2rcrm886gfmag">
                      {t('En son propre nom', 'On their own behalf')}
                    </div>
                    <div className="text-sm text-slate-500 mt-0.5" data-id="element-4887" data-mp="3fbijextlpvaa">
                      {t('Le citoyen fait la demande pour sa propre propriété.', 'The citizen is requesting for their own property.')}
                    </div>
                  </div>
                </label>

                <label className="flex items-start gap-3 p-4 border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors" data-id="element-4888" data-mp="235q3o8epsph">
                  <div className="pt-0.5" data-id="element-4889" data-mp="3k88z3k1huov6">
                    <input type="checkbox" checked={requesterConfig.family} onChange={e => setRequesterConfig({
                  ...requesterConfig,
                  family: e.target.checked
                })} className="w-4 h-4 text-[#4F46E5] rounded border-slate-300 focus:ring-[#4F46E5]" data-id="element-4890" data-mp="3p852pi6a4ea7" />
                  </div>
                  <div data-id="element-4891" data-mp="2fa5b5i2s7s3z">
                    <div className="text-sm font-semibold text-slate-900" data-id="element-4892" data-mp="2fane0xjy94kv">
                      {t('Pour un membre de la famille', 'For a family member')}
                    </div>
                    <div className="text-sm text-slate-500 mt-0.5" data-id="element-4893" data-mp="1ml6smjuqyny5">
                      {t('Permet de lier la demande au dossier d’un proche.', 'Allows linking the request to a relative’s file.')}
                    </div>
                  </div>
                </label>

                <label className="flex items-start gap-3 p-4 hover:bg-slate-50 cursor-pointer transition-colors" data-id="element-4894" data-mp="1rjrj3rk75t9e">
                  <div className="pt-0.5" data-id="element-4895" data-mp="13t5mcwfn7vep">
                    <input type="checkbox" checked={requesterConfig.other} onChange={e => setRequesterConfig({
                  ...requesterConfig,
                  other: e.target.checked
                })} className="w-4 h-4 text-[#4F46E5] rounded border-slate-300 focus:ring-[#4F46E5]" data-id="element-4896" data-mp="yw5hupyfz0pp" />
                  </div>
                  <div data-id="element-4897" data-mp="x96a65qcbjfx">
                    <div className="text-sm font-semibold text-slate-900" data-id="element-4898" data-mp="x7v582l5s5n8">
                      {t('En tant que mandataire (entrepreneur, etc.)', 'As a proxy (contractor, etc.)')}
                    </div>
                    <div className="text-sm text-slate-500 mt-0.5" data-id="element-4899" data-mp="1b0ug1r9zxbog">
                      {t('Exige une procuration ou une autorisation du propriétaire.', 'Requires a proxy or authorization from the owner.')}
                    </div>
                  </div>
                </label>
              </div>
            </div>}

          {/* FORM TAB */}
          {activeTab === 'form' && <div className="space-y-6" data-id="element-4900" data-mp="30jt0cydfoyy3">
              <div className="flex items-center justify-between" data-id="element-4901" data-mp="2vhk7s34h3ywi">
                <div data-id="element-4902" data-mp="2qjab1o33oq8s">
                  <h3 className="text-lg font-bold text-slate-900 mb-1" data-id="element-4903" data-mp="2qjshtene2muy">
                    {t('Étapes du formulaire', 'Form steps')}
                  </h3>
                  <p className="text-sm text-slate-500" data-id="element-4904" data-mp="3o93rrp8w9gzk">
                    {t('Découpez le formulaire en plusieurs étapes logiques.', 'Split the form into multiple logical steps.')}
                  </p>
                </div>
                <button className="flex items-center gap-2 px-3 py-1.5 bg-white border border-slate-200 text-slate-700 text-sm font-semibold rounded-lg hover:bg-slate-50 transition-colors" data-id="element-4905" data-mp="39g224jovw9ne">
                  <PlusIcon className="w-4 h-4" data-id="element-4906" data-mp="603cw9i8pprx" />
                  {t('Ajouter une étape', 'Add step')}
                </button>
              </div>

              <div className="space-y-3" data-id="element-4907" data-mp="3lpz53qcc3bqe">
                {formSteps.map((step, index) => <div key={step.id} className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-4 group" data-id="element-4908" data-mp="3kdsgcu98z2r" data-mpi={index}>
                    <button className="text-slate-300 hover:text-slate-500 cursor-grab" data-id="element-4909" data-mp="ckix4f82djd8">
                      <GripVerticalIcon className="w-5 h-5" data-id="element-4910" data-mp="8ixfyhohyix8" />
                    </button>
                    <div className="w-8 h-8 rounded-full bg-indigo-50 text-[#4F46E5] flex items-center justify-center text-sm font-bold flex-shrink-0" data-id="element-4911" data-mp="8h4jfv7llf2y">
                      {index + 1}
                    </div>
                    <div className="flex-1" data-id="element-4912" data-mp="w8jfj68s705r">
                      <input type="text" value={step.title} onChange={e => {
                  const newSteps = [...formSteps];
                  newSteps[index].title = e.target.value;
                  setFormSteps(newSteps);
                }} className="w-full font-semibold text-slate-900 bg-transparent border-none focus:outline-none focus:ring-0 p-0" data-id="element-4913" data-mp="w9bb4h2doxw1" />
                      <div className="text-xs text-slate-400 mt-1" data-id="element-4914" data-mp="15ukm2gv8umfa">
                        {t('3 champs configurés', '3 fields configured')}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-4915" data-mp="vyrd17gqhb29">
                      <button className="px-3 py-1.5 text-xs font-semibold text-[#4F46E5] bg-indigo-50 rounded-md hover:bg-indigo-100 transition-colors" data-id="element-4916" data-mp="i8e4b2w0psbv">
                        {t('Éditer les champs', 'Edit fields')}
                      </button>
                      <button className="p-1.5 text-slate-400 hover:text-red-600 rounded-md hover:bg-red-50 transition-colors" data-id="element-4917" data-mp="d7zubfauw8b7">
                        <TrashIcon className="w-4 h-4" data-id="element-4918" data-mp="3q90ym8nd3dfc" />
                      </button>
                    </div>
                  </div>)}
              </div>
            </div>}

          {/* PAYMENT TAB */}
          {activeTab === 'payment' && <div className="space-y-6" data-id="element-4919" data-mp="3bjv06mwclr8i">
              <div data-id="element-4920" data-mp="3bl66ouh6hd3b">
                <h3 className="text-lg font-bold text-slate-900 mb-1" data-id="element-4921" data-mp="3bhiatoo9gelf">
                  {t('Tarification et paiement', 'Pricing and payment')}
                </h3>
                <p className="text-sm text-slate-500" data-id="element-4922" data-mp="83xo28emm97j">
                  {t('Configurez les frais associés à ce permis.', 'Configure the fees associated with this permit.')}
                </p>
              </div>

              <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-6" data-id="element-4923" data-mp="2nu0q2d3eygry">
                <div className="flex items-center justify-between" data-id="element-4924" data-mp="2iu5zybrgpv7y">
                  <div data-id="element-4925" data-mp="2ix0ke3ah7g1d">
                    <div className="font-semibold text-slate-900" data-id="element-4926" data-mp="yd572m6kw92c">
                      {t('Exiger un paiement', 'Require payment')}
                    </div>
                    <div className="text-sm text-slate-500 mt-0.5" data-id="element-4927" data-mp="139n9hfefrvbi">
                      {t('Activer la facturation pour ce type de permis.', 'Enable billing for this permit type.')}
                    </div>
                  </div>
                  <button type="button" role="switch" aria-checked={paymentConfig.enabled} onClick={() => setPaymentConfig({
                ...paymentConfig,
                enabled: !paymentConfig.enabled
              })} className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-[#4F46E5] focus:ring-offset-2 ${paymentConfig.enabled ? 'bg-[#4F46E5]' : 'bg-slate-200'}`} data-id="element-4928" data-mp="1i2ytslufrxzp">
                    <span aria-hidden="true" className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${paymentConfig.enabled ? 'translate-x-5' : 'translate-x-0'}`} data-id="element-4929" data-mp="1n6r86rpcjzop" />
                  </button>
                </div>

                {paymentConfig.enabled && <>
                    <div className="pt-4 border-t border-slate-100" data-id="element-4930" data-mp="1i5tsqmvq1jqw">
                      <label className="block text-sm font-semibold text-slate-700 mb-2" data-id="element-4931" data-mp="1i74xaowzqvj2">
                        {t('Montant de base ($)', 'Base amount ($)')}
                      </label>
                      <input type="number" value={paymentConfig.amount} onChange={e => setPaymentConfig({
                  ...paymentConfig,
                  amount: e.target.value
                })} placeholder="0.00" className="w-full max-w-xs px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#4F46E5] focus:border-transparent" data-id="element-4932" data-mp="pmwljuyswhpe" />
                    </div>

                    <div className="pt-4 border-t border-slate-100" data-id="element-4933" data-mp="z8s14xd12w7r">
                      <label className="block text-sm font-semibold text-slate-700 mb-3" data-id="element-4934" data-mp="z6p9ya052x1g">
                        {t('Moment du paiement', 'Payment timing')}
                      </label>
                      <div className="space-y-3" data-id="element-4935" data-mp="p915gscqeb63">
                        <label className="flex items-center gap-3 cursor-pointer" data-id="element-4936" data-mp="351ql9ef05uh6">
                          <input type="radio" name="timing" checked={paymentConfig.timing === 'upfront'} onChange={() => setPaymentConfig({
                      ...paymentConfig,
                      timing: 'upfront'
                    })} className="w-4 h-4 text-[#4F46E5] border-slate-300 focus:ring-[#4F46E5]" data-id="element-4937" data-mp="3a0uw09hks09m" />
                          <div data-id="element-4938" data-mp="ws1yukttiofq">
                            <div className="text-sm font-medium text-slate-900" data-id="element-4939" data-mp="ss9xot6r7kke">
                              {t('Lors du dépôt de la demande', 'Upon request submission')}
                            </div>
                            <div className="text-xs text-slate-500" data-id="element-4940" data-mp="2i7zmp3c0dx29">
                              {t('Le citoyen doit payer avant de soumettre.', 'Citizen must pay before submitting.')}
                            </div>
                          </div>
                        </label>
                        <label className="flex items-center gap-3 cursor-pointer" data-id="element-4941" data-mp="2ui8t01tmwdtt">
                          <input type="radio" name="timing" checked={paymentConfig.timing === 'later'} onChange={() => setPaymentConfig({
                      ...paymentConfig,
                      timing: 'later'
                    })} className="w-4 h-4 text-[#4F46E5] border-slate-300 focus:ring-[#4F46E5]" data-id="element-4942" data-mp="2ukbhuij1w0wy" />
                          <div data-id="element-4943" data-mp="3n64pp6j7e5ql">
                            <div className="text-sm font-medium text-slate-900" data-id="element-4944" data-mp="3s5plx96znzzm">
                              {t('Plus tard dans le processus', 'Later in the process')}
                            </div>
                            <div className="text-xs text-slate-500" data-id="element-4945" data-mp="qu71vnghp5le">
                              {t('La facture sera générée par un agent après analyse.', 'Invoice will be generated by an agent after analysis.')}
                            </div>
                          </div>
                        </label>
                      </div>
                    </div>
                  </>}
              </div>
            </div>}

          {/* SETTINGS TAB */}
          {activeTab === 'settings' && <div className="space-y-6" data-id="element-4946" data-mp="3bjwfjx5gilci">
              <div data-id="element-4947" data-mp="3bkxzys8d4k6w">
                <h3 className="text-lg font-bold text-slate-900 mb-1" data-id="element-4948" data-mp="3bminvz0694vs">
                  {t('Configuration générale', 'General settings')}
                </h3>
                <p className="text-sm text-slate-500" data-id="element-4949" data-mp="1m4lpjrfafoin">
                  {t('Associez ce permis au bon département et processus.', 'Link this permit to the correct department and workflow.')}
                </p>
              </div>

              <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-6" data-id="element-4950" data-mp="1hbiutfyry90v">
                <div data-id="element-4951" data-mp="13cjeqwpbv2gb">
                  <label className="block text-sm font-semibold text-slate-700 mb-2" data-id="element-4952" data-mp="13b818562ygc0">
                    {t('Département responsable', 'Responsible department')}
                  </label>
                  <select value={settingsConfig.department} onChange={e => setSettingsConfig({
                ...settingsConfig,
                department: e.target.value
              })} className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#4F46E5] focus:border-transparent bg-white" data-id="element-4953" data-mp="tfwxehw7mtj1">
                    <option data-id="element-4954" data-mp="4y16xpzk0oa0">Urbanisme</option>
                    <option data-id="element-4955" data-mp="3nb7fvdpcm2a3">Permis et inspections</option>
                    <option data-id="element-4956" data-mp="3ncsaf3bnxl7y">Génie</option>
                    <option data-id="element-4957" data-mp="3sabnw10jdtyu">Travaux publics</option>
                  </select>
                </div>

                <div className="pt-4 border-t border-slate-100" data-id="element-4958" data-mp="juihiezx5yvi">
                  <div className="flex items-center justify-between mb-2" data-id="element-4959" data-mp="jva2ywzsyg82">
                    <label className="block text-sm font-semibold text-slate-700" data-id="element-4960" data-mp="nv2cbbx0sqeh">
                      {t('Processus (Workflow)', 'Workflow')}
                    </label>
                    <button type="button" onClick={() => setShowCreateProcessus(true)} className="text-xs font-medium text-[#4F46E5] hover:text-[#4338CA] flex items-center gap-1" data-id="element-4961" data-mp="euw527rx0mnr">
                      <PlusIcon className="w-3.5 h-3.5" data-id="element-4962" data-mp="2zja41mlwfa8z" />
                      {t('Créer un processus', 'Create workflow')}
                    </button>
                  </div>
                  <select value={settingsConfig.workflow} onChange={e => setSettingsConfig({
                ...settingsConfig,
                workflow: e.target.value
              })} className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#4F46E5] focus:border-transparent bg-white" data-id="element-4963" data-mp="2n2ixt1cngmsa">
                    <option data-id="element-4964" data-mp="2i5ircpbw1i0n">Permis de construction (standard)</option>
                    <option data-id="element-4965" data-mp="2dczdrsd7amvu">Processus simplifié</option>
                    <option data-id="element-4966" data-mp="2dbefbrnrmjsp">Licence / occupation</option>
                  </select>
                  <div className="mt-3 p-3 bg-slate-50 rounded-lg border border-slate-200" data-id="element-4967" data-mp="1plk4ffn0b5b3">
                    <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2" data-id="element-4968" data-mp="1koa7zg179tdr">
                      {t('Étapes incluses :', 'Included steps:')}
                    </div>
                    <div className="flex flex-wrap gap-2" data-id="element-4969" data-mp="3po1t90pmkn7v">
                      {settingsConfig.workflow === 'Permis de construction (standard)' ? <>
                          <span className="px-2 py-1 bg-slate-200 text-slate-700 text-xs rounded font-medium" data-id="element-4970" data-mp="39icixd6gvy8">
                            Nouvelle demande
                          </span>
                          <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs rounded font-medium" data-id="element-4971" data-mp="36rk2ommiitd7">
                            Vérification
                          </span>
                          <span className="px-2 py-1 bg-indigo-100 text-indigo-700 text-xs rounded font-medium" data-id="element-4972" data-mp="3fshzokyja0ma">
                            Analyse
                          </span>
                          <span className="px-2 py-1 bg-emerald-100 text-emerald-700 text-xs rounded font-medium" data-id="element-4973" data-mp="v0pwdscbstqz">
                            Émis
                          </span>
                        </> : <span className="text-sm text-slate-500" data-id="element-4974" data-mp="2biqaqblrlzfe">...</span>}
                    </div>
                  </div>
                </div>
              </div>
            </div>}
        </div>
      </div>

      <CreateProcessusDialog open={showCreateProcessus} onClose={() => setShowCreateProcessus(false)} data-id="element-4975" data-mp="1wsshvicw4ljv" />
    </div>;
}